/**
 * Save or update user in database
 * @param {Object} payload - User data
 * @returns {Promise<Object>} { status, user?, msg? } - throws if request failed
 */
export const saveUser = async (payload) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  if (!baseUrl) {
    throw new Error('REACT_APP_BASE_URL is not set. Check .env.');
  }
  const response = await fetch(`${baseUrl}/users/create-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.msg || data.message || `Request failed (${response.status})`);
  }
  if (!data.user) {
    throw new Error(data.msg || 'Server did not return user data');
  }
  return data;
};

/**
 * Check if user has verified phone number
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>}
 */
export const checkUserPhoneVerified = async (uid) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/check-phone/${uid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Check phone verified error:', error);
    return null;
  }
};

/**
 * Update user's phone number and verification status
 * @param {string} uid - User ID
 * @param {string} phoneNumber - Phone number
 * @param {boolean} phoneVerified - Verification status
 * @returns {Promise<Object>}
 */
export const updateUserPhone = async (uid, phoneNumber, phoneVerified = true) => {
  const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/update-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uid, phoneNumber, phoneVerified }),
  });
  return await response.json();
};

/**
 * Get user by email (for account linking)
 * @param {string} email - User email
 * @returns {Promise<Object|null>}
 */
export const getUserByEmail = async (email) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/users/by-email/${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Get user by email error:', error);
    return null;
  }
};
